/**
 * Sealing layers (F4, ADR-12; privacy spec §4).
 *
 * Broadcast (`sealedBroadcast.ciphertext`): AES-256-GCM with the 32-byte epoch
 * content key. Wire layout `nonce(12) || ct || tag(16)`, AAD = utf8(keyRef).
 *
 * Need-to-know (`sealedNeedToKnow.ciphertext`) — BNK1:
 *   "BNK1" | u8 n | n × (enc(65) || sealedCek(48)) | nonce(12) | payloadCt
 * Slot i belongs to recipients[i] (envelope-level lexicon array; order is
 * shared state). Per-slot HPKE: info = utf8("beacon/bnk1/v1"),
 * aad = utf8(subjectTid); payload AES-256-GCM(CEK), AAD = utf8(subjectTid)
 * (anti-splice, both layers). The CEK is random per envelope and never stored.
 */
import { b64urlDecode, concatBytes, utf8 } from "./bytes.js";
import { jwkFromDidKey, type Jwk } from "./didkey.js";
import { hpkeOpen, hpkeSeal } from "./hpke.js";
import type { CryptoKey } from "./sign.js";

const NONCE_LEN = 12;
const TAG_LEN = 16;
const ENC_LEN = 65;
const SEALED_CEK_LEN = 32 + TAG_LEN;
const SLOT_LEN = ENC_LEN + SEALED_CEK_LEN; // 113
const MAGIC = utf8("BNK1");
const BNK1_INFO = utf8("beacon/bnk1/v1");
const MAX_FIELD = 16384; // lexicon bytes bound on sealed ciphertext fields

async function importAesKey(key: Uint8Array, usage: "encrypt" | "decrypt"): Promise<CryptoKey> {
  if (key.length !== 32) throw new Error("seal: content key must be 32 bytes");
  return crypto.subtle.importKey("raw", key, { name: "AES-GCM" }, false, [usage]);
}

async function aesGcmSeal(
  key32: Uint8Array,
  nonce: Uint8Array,
  aad: Uint8Array,
  pt: Uint8Array,
): Promise<Uint8Array> {
  const k = await importAesKey(key32, "encrypt");
  return new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce, additionalData: aad }, k, pt),
  );
}

async function aesGcmOpen(
  key32: Uint8Array,
  nonce: Uint8Array,
  aad: Uint8Array,
  ct: Uint8Array,
): Promise<Uint8Array> {
  const k = await importAesKey(key32, "decrypt");
  try {
    return new Uint8Array(
      await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce, additionalData: aad }, k, ct),
    );
  } catch {
    throw new Error("seal: open failed (AEAD tag mismatch)");
  }
}

/**
 * Seal a broadcast field: `nonce(12) || ct || tag(16)`, AAD = utf8(keyRef).
 * `testNonce` is test-only: fixed nonces destroy security if used in
 * production paths — only vector generation/verification may pass it.
 */
export async function sealBroadcast(
  contentKey32: Uint8Array,
  keyRef: string,
  pt: Uint8Array,
  testNonce?: Uint8Array,
): Promise<Uint8Array> {
  const nonce = testNonce ?? crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  if (nonce.length !== NONCE_LEN) throw new Error("seal: nonce must be 12 bytes");
  const field = concatBytes(nonce, await aesGcmSeal(contentKey32, nonce, utf8(keyRef), pt));
  if (field.length > MAX_FIELD) throw new Error("seal: broadcast field exceeds 16384 bytes");
  return field;
}

export async function openBroadcast(
  contentKey32: Uint8Array,
  keyRef: string,
  field: Uint8Array,
): Promise<Uint8Array> {
  if (field.length > MAX_FIELD) throw new Error("seal: broadcast field exceeds 16384 bytes");
  if (field.length < NONCE_LEN + TAG_LEN) throw new Error("seal: broadcast field truncated");
  return aesGcmOpen(contentKey32, field.slice(0, NONCE_LEN), utf8(keyRef), field.slice(NONCE_LEN));
}

export interface NeedToKnowRecipient {
  did: string;
  /** did:key of the recipient's encryption key. */
  encryptionKey: string;
}

/** did:key → 65-byte uncompressed point (decompress via jwkFromDidKey). */
function point65FromDidKey(didKey: string): Uint8Array {
  const jwk = jwkFromDidKey(didKey);
  return concatBytes(new Uint8Array([0x04]), b64urlDecode(jwk.x), b64urlDecode(jwk.y));
}

/**
 * Seal a BNK1 need-to-know field. The CEK is random per envelope (injectable
 * for vectors) and never stored. `testHooks` is test-only: fixed ephemeral
 * keys / nonces / CEKs destroy security if used in production paths — only
 * vector generation/verification may pass it.
 */
export async function sealNeedToKnow(
  recipients: NeedToKnowRecipient[],
  subjectTid: string,
  pt: Uint8Array,
  testHooks?: { cek?: Uint8Array; payloadNonce?: Uint8Array; slotSkEs?: Jwk[] },
): Promise<Uint8Array> {
  if (recipients.length < 1 || recipients.length > 255) {
    throw new Error("seal: BNK1 requires 1..255 recipients");
  }
  const cek = testHooks?.cek ?? crypto.getRandomValues(new Uint8Array(32));
  if (cek.length !== 32) throw new Error("seal: CEK must be 32 bytes");
  const aad = utf8(subjectTid);

  const slots: Uint8Array[] = [];
  for (let i = 0; i < recipients.length; i++) {
    const pkR = point65FromDidKey(recipients[i].encryptionKey);
    const { enc, ct } = await hpkeSeal(pkR, BNK1_INFO, aad, cek, {
      testSkE: testHooks?.slotSkEs?.[i],
    });
    if (enc.length !== ENC_LEN || ct.length !== SEALED_CEK_LEN) {
      throw new Error("seal: malformed BNK1 slot");
    }
    slots.push(concatBytes(enc, ct));
  }

  const nonce = testHooks?.payloadNonce ?? crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  if (nonce.length !== NONCE_LEN) throw new Error("seal: nonce must be 12 bytes");
  const payloadCt = await aesGcmSeal(cek, nonce, aad, pt);

  const field = concatBytes(MAGIC, new Uint8Array([recipients.length]), ...slots, nonce, payloadCt);
  if (field.length > MAX_FIELD) throw new Error("seal: BNK1 field exceeds 16384 bytes");
  return field;
}

/** Open a BNK1 field as `myDid`, locating our slot by position in the envelope-order recipients array. */
export async function openNeedToKnow(
  field: Uint8Array,
  recipients: string[],
  myDid: string,
  myEncryptionPrivateKey: CryptoKey,
  myEncryptionDidKey: string,
  subjectTid: string,
): Promise<Uint8Array> {
  if (field.length > MAX_FIELD) throw new Error("seal: BNK1 field exceeds 16384 bytes");
  if (field.length < MAGIC.length + 1) throw new Error("seal: BNK1 field truncated");
  for (let i = 0; i < MAGIC.length; i++) {
    if (field[i] !== MAGIC[i]) throw new Error("seal: bad BNK1 magic");
  }
  const n = field[MAGIC.length];
  if (n < 1) throw new Error("seal: BNK1 slot count must be >= 1");
  if (n !== recipients.length) {
    throw new Error("seal: BNK1 slot count disagrees with recipients[].length");
  }
  const slotsEnd = MAGIC.length + 1 + n * SLOT_LEN;
  if (field.length < slotsEnd + NONCE_LEN + TAG_LEN) throw new Error("seal: BNK1 field truncated");

  const i = recipients.indexOf(myDid);
  if (i < 0) throw new Error("seal: not-a-recipient");

  const slotStart = MAGIC.length + 1 + i * SLOT_LEN;
  const enc = field.slice(slotStart, slotStart + ENC_LEN);
  const sealedCek = field.slice(slotStart + ENC_LEN, slotStart + SLOT_LEN);
  const aad = utf8(subjectTid);
  const cek = await hpkeOpen(
    myEncryptionPrivateKey,
    point65FromDidKey(myEncryptionDidKey),
    enc,
    BNK1_INFO,
    aad,
    sealedCek,
  );

  const nonce = field.slice(slotsEnd, slotsEnd + NONCE_LEN);
  return aesGcmOpen(cek, nonce, aad, field.slice(slotsEnd + NONCE_LEN));
}
